import React, { useRef, useState } from 'react';
import Card from './Card';
import './HandFan.css';

/** Perfect arc: card bottom centers on a circle, equal angle spacing. Larger radius = gentler curve. */
const ARC_RADIUS = 260;
/** Smaller arc for opponent seats on the table (still reads as a 10-card fan). */
const ARC_RADIUS_COMPACT = 88;
const DRAG_THRESHOLD_PX = 8;

export default function HandFan({
  cards,
  selectedCardId,
  multiSelectedIds,
  hiddenCardIds = [],
  onSelect,
  onReorder,
  onDragDiscard,
  onDragDiscardBlocked,
  /** (x, y) => boolean; true when dropping near center discard zone. */
  isDiscardDropTarget,
  /** (x, y) -> pile index or null — drop hand card onto existing pile */
  getPileDropIndex,
  onDropOnPile,
  onDragOverPileChange,
  discardDisabledId,
  interactive = true,
  /** During deal: show only card backs (no faces / no drag). */
  faceDown = false,
  /** Narrower fan with smaller cards (e.g. opponents on PokerTable). */
  compact = false,
}) {
  const arcRadius = compact ? ARC_RADIUS_COMPACT : ARC_RADIUS;
  const fanRef = useRef(null);
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [dragTargetIndex, setDragTargetIndex] = useState(-1);
  const [dragDeltaX, setDragDeltaX] = useState(0);
  const [dragDeltaY, setDragDeltaY] = useState(0);
  const [isDraggingOut, setIsDraggingOut] = useState(false);
  const lastTargetIndexRef = useRef(-1);
  const dragStartRef = useRef(null);
  const didDragRef = useRef(false);
  const [suppressClick, setSuppressClick] = useState(false);

  const visualOrder =
    draggingCardId != null && dragTargetIndex >= 0
      ? (() => {
          const order = [...cards];
          const from = order.findIndex((c) => c.id === draggingCardId);
          if (from < 0) return cards;
          const [removed] = order.splice(from, 1);
          order.splice(dragTargetIndex, 0, removed);
          return order;
        })()
      : cards;

  const total = cards.length;
  const spreadDeg =
    total <= 1
      ? 0
      : compact
        ? Math.min(38, Math.max(18, total * 3.4))
        : Math.min(50, Math.max(24, total * 8));
  const startAngle = total <= 1 ? 0 : -spreadDeg / 2;
  const angleStep = total <= 1 ? 0 : spreadDeg / Math.max(1, total - 1);

  const getIndexCenterX = (rect, index, totalCount) => {
    const angleDeg = totalCount <= 1 ? 0 : startAngle + angleStep * index;
    const angleRad = (angleDeg * Math.PI) / 180;
    const xOffset = totalCount <= 1 ? 0 : arcRadius * Math.sin(angleRad);
    return rect.left + rect.width / 2 + xOffset;
  };

  const getTargetIndexFromX = (clientX, rect, totalCount, offsetX) => {
    const targetCenterX = clientX - offsetX;
    let closestIndex = 0;
    let minDist = Infinity;
    for (let i = 0; i < totalCount; i += 1) {
      const centerX = getIndexCenterX(rect, i, totalCount);
      const dist = Math.abs(targetCenterX - centerX);
      if (dist < minDist) {
        minDist = dist;
        closestIndex = i;
      }
    }
    return closestIndex;
  };

  const handleCardPointerDown = (index, card) => (e) => {
    if (!interactive || faceDown) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const cardId = card.id;
    const rect = fanRef.current?.getBoundingClientRect();
    const totalCards = cardsRef.current.length;
    const startCenterX = rect ? getIndexCenterX(rect, index, totalCards) : e.clientX;
    lastTargetIndexRef.current = index;
    setDragTargetIndex(index);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      cardId,
      offsetX: e.clientX - startCenterX,
    };
    didDragRef.current = false;

    let draggingOut = false;

    const pointerId = e.pointerId;
    const onPointerMove = (ev) => {
      if (ev.pointerId !== pointerId) return;
      const start = dragStartRef.current;
      if (!start) return;
      const dx = ev.clientX - start.x;
      const dy = ev.clientY - start.y;
      if (!didDragRef.current) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        didDragRef.current = true;
        setDraggingCardId(cardId);
      }
      if (didDragRef.current && ev.pointerType !== 'mouse') {
        // Prevent page scrolling while dragging on touch devices.
        ev.preventDefault();
      }
      if (!didDragRef.current) return;
      setDragDeltaX(dx);
      setDragDeltaY(dy);

      const rect = fanRef.current?.getBoundingClientRect();
      const outsideFan = rect
        ? ev.clientX < rect.left || ev.clientX > rect.right || ev.clientY < rect.top || ev.clientY > rect.bottom
        : false;
      if (outsideFan !== draggingOut) {
        draggingOut = outsideFan;
        setIsDraggingOut(outsideFan);
      }

      if (!outsideFan) {
        const totalCards = cardsRef.current.length;
        if (rect && totalCards > 1) {
          const targetIndex = getTargetIndexFromX(ev.clientX, rect, totalCards, start.offsetX || 0);
          setDragTargetIndex(targetIndex);
          lastTargetIndexRef.current = targetIndex;
        }
      }

      if (getPileDropIndex && onDropOnPile && didDragRef.current) {
        const pi = getPileDropIndex(ev.clientX, ev.clientY);
        onDragOverPileChange?.(pi ?? null);
      } else if (onDragOverPileChange && didDragRef.current) {
        onDragOverPileChange(null);
      }
    };

    const cleanup = () => {
      onDragOverPileChange?.(null);
      setDraggingCardId(null);
      setDragTargetIndex(-1);
      setDragDeltaX(0);
      setDragDeltaY(0);
      setIsDraggingOut(false);
      lastTargetIndexRef.current = null;
      dragStartRef.current = null;
      didDragRef.current = false;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };

    const onPointerUp = (ev) => {
      if (ev.pointerId !== pointerId) return;
      const fromIndex = cardsRef.current.findIndex((c) => c.id === cardId);
      const toIndex = lastTargetIndexRef.current;
      const cx = ev.clientX;
      const cy = ev.clientY;
      if (didDragRef.current) {
        setSuppressClick(true);
        const pileIdx = getPileDropIndex && onDropOnPile ? getPileDropIndex(cx, cy) : null;
        const discardTargeted = Boolean(isDiscardDropTarget?.(cx, cy));
        if (pileIdx != null && onDropOnPile) {
          const card = cardsRef.current.find((c) => c.id === cardId);
          if (card) onDropOnPile(card, pileIdx);
        } else if (discardTargeted && onDragDiscard && discardDisabledId !== cardId) {
          const card = cardsRef.current.find((c) => c.id === cardId);
          if (card) onDragDiscard(card);
        } else if (discardTargeted && onDragDiscardBlocked && discardDisabledId === cardId) {
          onDragDiscardBlocked();
        } else if (!draggingOut && fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
          onReorder(fromIndex, toIndex);
        }
        setTimeout(() => setSuppressClick(false), 0);
      }
      cleanup();
    };

    const onPointerCancel = (ev) => {
      if (ev.pointerId !== pointerId) return;
      cleanup();
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
  };

  const isReordering = draggingCardId != null;

  const dragStartIndex = isReordering
    ? cards.findIndex((c) => c.id === draggingCardId)
    : -1;
  const dragOrigAngleDeg = dragStartIndex >= 0 && total > 1
    ? startAngle + angleStep * dragStartIndex
    : 0;
  const dragOrigAngleRad = (dragOrigAngleDeg * Math.PI) / 180;
  const dragOrigX = total <= 1 ? 0 : arcRadius * Math.sin(dragOrigAngleRad);
  const dragOrigY = total <= 1 ? 0 : arcRadius * (1 - Math.cos(dragOrigAngleRad));

  return (
    <div
      ref={fanRef}
      className={`hand-fan hand-fan--no-drag-ghost ${compact ? 'hand-fan--compact' : ''} ${isReordering ? 'hand-fan--reordering' : ''}`}
      role="list"
    >
      {visualOrder.map((card, index) => {
        const angleDeg = total <= 1 ? 0 : startAngle + angleStep * index;
        const isSelected = multiSelectedIds ? multiSelectedIds.includes(card.id) : selectedCardId === card.id;
        const isDragging = draggingCardId === card.id;
        const isHidden = hiddenCardIds.includes(card.id);
        const angleRad = (angleDeg * Math.PI) / 180;
        const xOffset = total <= 1 ? 0 : arcRadius * Math.sin(angleRad);
        const yOffset = total <= 1 ? 0 : arcRadius * (1 - Math.cos(angleRad));

        const fanW = fanRef.current?.offsetWidth || 480;
        const halfFan = fanW / 2 - 36;

        let cardTransform;
        if (isDragging && isDraggingOut) {
          const dx = dragOrigX + dragDeltaX;
          const dy = dragOrigY + dragDeltaY;
          cardTransform = `translate(-50%, 0) translate(${dx}px, ${dy}px) rotate(0deg)`;
        } else if (isDragging) {
          const dx = Math.max(-halfFan, Math.min(halfFan, dragOrigX + dragDeltaX));
          const t = total <= 1 ? 0 : Math.asin(Math.max(-1, Math.min(1, dx / arcRadius)));
          const curveY = arcRadius * (1 - Math.cos(t));
          const curveDeg = (t * 180) / Math.PI;
          cardTransform = `translate(-50%, 0) translate(${dx}px, ${curveY - 10}px) rotate(${curveDeg}deg)`;
        } else {
          cardTransform = `translate(-50%, 0) translate(${xOffset}px, ${yOffset}px) rotate(${angleDeg}deg)`;
        }

        return (
          <div
            key={card.id}
            className={`hand-fan__card ${isSelected ? 'hand-fan__card--selected' : ''} ${!interactive ? 'hand-fan__card--readonly' : ''} ${isDragging && !isDraggingOut ? 'hand-fan__card--dragging' : ''} ${isDragging && isDraggingOut ? 'hand-fan__card--dragging-out' : ''} ${isHidden ? 'hand-fan__card--hidden' : ''}`}
            data-hand-card-id={card.id}
            style={{
              left: '50%',
              transform: cardTransform,
            }}
            onPointerDown={handleCardPointerDown(index, card)}
            role="listitem"
          >
            <Card
              card={card}
              faceUp={!faceDown}
              selected={!faceDown && isSelected && !isDragging}
              dragging={isDragging}
              onClick={interactive && !faceDown && !suppressClick ? () => onSelect(card) : undefined}
              disabled={!interactive || faceDown}
            />
          </div>
        );
      })}
    </div>
  );
}
